import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  getQuickBooksStatus,
  connectQuickBooks,
  disconnectQuickBooks,
  getQuickBooksItems,
  syncQuickBooksItems,
  getQBSettings,
  updateQBSettings,
  getQBSyncStatus,
  retryQBSync,
  getQBCustomers,
  linkQBCustomer,
  autoMatchQBCustomers,
  aiMatchQBCustomers,
  unlinkAllQBCustomers,
  QuickBooksStatus,
  QuickBooksItem,
  QuickBooksSettings as QBSettingsType,
  QBSyncStatus,
  QBCustomer,
  AppCustomerMapping,
} from '../../services/quickbooksService';

const QuickBooksSettings: React.FC = () => {
  const [status, setStatus] = useState<QuickBooksStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Items & Settings state
  const [items, setItems] = useState<QuickBooksItem[]>([]);
  const [qbSettings, setQbSettings] = useState<QBSettingsType | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [syncStatus, setSyncStatus] = useState<QBSyncStatus | null>(null);
  const [retrying, setRetrying] = useState(false);

  // Customer mapping state
  const [qbCustomers, setQbCustomers] = useState<QBCustomer[]>([]);
  const [appCustomers, setAppCustomers] = useState<AppCustomerMapping[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [autoMatching, setAutoMatching] = useState(false);
  const [unlinking, setUnlinking] = useState(false);
  const [matchConfidences, setMatchConfidences] = useState<Record<number, string>>({});
  const [customerFilter, setCustomerFilter] = useState<'all' | 'linked' | 'unlinked'>('unlinked');
  const [customerSearch, setCustomerSearch] = useState('');
  const [qbDropdownOpen, setQbDropdownOpen] = useState<number | null>(null);
  const [qbDropdownSearch, setQbDropdownSearch] = useState('');
  const [qbNameLookup, setQbNameLookup] = useState<Record<string, string>>({});
  const [aiMatching, setAiMatching] = useState(false);
  const [aiProposals, setAiProposals] = useState<Array<{app_id: number; app_name: string; qb_id: string; qb_name: string; confidence: string; reason: string}>>([]);
  const [acceptedProposals, setAcceptedProposals] = useState<Set<number>>(new Set());
  const [rejectedProposals, setRejectedProposals] = useState<Set<number>>(new Set());

  const loadStatus = useCallback(async () => {
    try {
      const data = await getQuickBooksStatus();
      setStatus(data);

      // If connected, also load items, settings, and sync status
      if (data.connected) {
        try {
          const [itemsData, settingsData, syncData] = await Promise.all([
            getQuickBooksItems({ active_only: true }),
            getQBSettings(),
            getQBSyncStatus()
          ]);
          setItems(itemsData);
          setQbSettings(settingsData);
          setSyncStatus(syncData);
        } catch (error) {
          console.error('Failed to load items/settings:', error);
        }
      }
    } catch (error) {
      console.error('Failed to load QuickBooks status:', error);
      setMessage({ type: 'error', text: 'Failed to load QuickBooks connection status' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();

    // Check for OAuth callback success/error in URL
    const urlParams = new URLSearchParams(window.location.search);
    const qbSuccess = urlParams.get('qb_connected');
    const qbError = urlParams.get('qb_error');

    if (qbSuccess === 'true') {
      setMessage({ type: 'success', text: 'QuickBooks connected successfully!' });
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (qbError) {
      setMessage({ type: 'error', text: `QuickBooks connection failed: ${qbError}` });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [loadStatus]);

  const handleConnect = async () => {
    setConnecting(true);
    setMessage(null);

    try {
      const result = await connectQuickBooks();
      // Redirect to QuickBooks OAuth page
      window.location.href = result.authorization_url;
    } catch (error) {
      console.error('Failed to start QuickBooks connection:', error);
      setMessage({ type: 'error', text: 'Failed to start QuickBooks connection' });
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect QuickBooks? This will stop invoice syncing.')) {
      return;
    }

    setDisconnecting(true);
    setMessage(null);

    try {
      await disconnectQuickBooks();
      setMessage({ type: 'success', text: 'QuickBooks disconnected successfully' });
      await loadStatus();
    } catch (error) {
      console.error('Failed to disconnect QuickBooks:', error);
      setMessage({ type: 'error', text: 'Failed to disconnect QuickBooks' });
    } finally {
      setDisconnecting(false);
    }
  };

  const handleSyncItems = async () => {
    setSyncing(true);
    setMessage(null);

    try {
      const result = await syncQuickBooksItems();
      const productInfo = result.products_created != null
        ? ` (${result.products_created} new products, ${result.products_updated} updated)`
        : '';
      setMessage({ type: 'success', text: `Synced ${result.synced_count} items from QuickBooks${productInfo}` });
      // Reload items
      const itemsData = await getQuickBooksItems({ active_only: true });
      setItems(itemsData);
    } catch (error) {
      console.error('Failed to sync items:', error);
      setMessage({ type: 'error', text: 'Failed to sync items from QuickBooks' });
    } finally {
      setSyncing(false);
    }
  };

  const handleDefaultItemChange = async (field: string, qbId: string | null) => {
    if (!qbSettings) return;

    setSavingSettings(true);
    setMessage(null);

    try {
      // Find the local DB ID for this QB item
      const item = items.find(i => i.qb_id === qbId);
      const updateData: Partial<QBSettingsType> = {
        [field]: item?.id || null
      };

      const updated = await updateQBSettings(updateData);
      setQbSettings(updated);
      setMessage({ type: 'success', text: 'Default item updated' });
    } catch (error) {
      console.error('Failed to update default item:', error);
      setMessage({ type: 'error', text: 'Failed to update default item' });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleRetrySync = async () => {
    setRetrying(true);
    setMessage(null);

    try {
      const result = await retryQBSync();
      const total = result.invoices_synced + result.customers_synced;
      const failed = result.invoices_failed + result.customers_failed;
      setMessage({
        type: failed > 0 ? 'error' : 'success',
        text: `Synced ${total} records${failed > 0 ? `, ${failed} failed` : ''}`
      });
      // Refresh sync status
      const syncData = await getQBSyncStatus();
      setSyncStatus(syncData);
    } catch (error) {
      console.error('Failed to retry sync:', error);
      setMessage({ type: 'error', text: 'Failed to retry sync' });
    } finally {
      setRetrying(false);
    }
  };

  const handleLoadCustomers = async () => {
    setLoadingCustomers(true);
    setMessage(null);
    try {
      const data = await getQBCustomers();
      setQbCustomers(data.qb_customers);
      setAppCustomers(data.app_customers);
      // Build QB name lookup so linked customers always show names
      const lookup: Record<string, string> = {};
      (data.qb_customers || []).forEach(q => { lookup[q.id] = q.display_name; });
      setQbNameLookup(lookup);
    } catch (error) {
      console.error('Failed to load QB customers:', error);
      setMessage({ type: 'error', text: 'Failed to load QuickBooks customers' });
    } finally {
      setLoadingCustomers(false);
    }
  };

  const handleAutoMatch = async () => {
    setAutoMatching(true);
    setMessage(null);
    try {
      const result = await autoMatchQBCustomers();
      // Store confidence levels for matched customers
      const newConfidences: Record<number, string> = {};
      (result.matches || []).forEach((m: { customer_id: number; confidence: string }) => {
        newConfidences[m.customer_id] = m.confidence;
      });
      setMatchConfidences(prev => ({ ...prev, ...newConfidences }));
      setMessage({
        type: 'success',
        text: `Auto-matched ${result.newly_matched} customers (${result.already_linked} already linked, ${result.unmatched_count} need manual matching)`
      });
      // Refresh customer list
      await handleLoadCustomers();
    } catch (error) {
      console.error('Failed to auto-match:', error);
      setMessage({ type: 'error', text: 'Failed to auto-match customers' });
    } finally {
      setAutoMatching(false);
    }
  };

  const handleAIMatch = async () => {
    setAiMatching(true);
    setAiProposals([]);
    setAcceptedProposals(new Set());
    setRejectedProposals(new Set());
    setMessage(null);
    try {
      const result = await aiMatchQBCustomers();
      // Separate: exact matches were auto-saved, AI proposals need review
      const proposals = (result.proposals || []).filter(p => !p.reason.startsWith('Exact '));
      setAiProposals(proposals);
      setMessage({
        type: 'success',
        text: `Exact matches: ${result.exact_matched} auto-saved. AI found ${result.ai_proposed} additional matches for review. ${result.unmatched_count} still unmatched.`
      });
      // Refresh customer list to reflect exact matches
      await handleLoadCustomers();
    } catch (error) {
      console.error('Failed to AI match:', error);
      setMessage({ type: 'error', text: 'AI matching failed. Check that ANTHROPIC_API_KEY is configured.' });
    } finally {
      setAiMatching(false);
    }
  };

  const handleAcceptProposal = async (proposal: {app_id: number; app_name: string; qb_id: string; qb_name: string; confidence: string; reason: string}) => {
    try {
      await linkQBCustomer(proposal.app_id, proposal.qb_id);
      setAcceptedProposals(prev => { const n = new Set(Array.from(prev)); n.add(proposal.app_id); return n; });
      // Update local state
      setAppCustomers(prev =>
        prev.map(c => c.id === proposal.app_id ? { ...c, qb_customer_id: proposal.qb_id } : c)
      );
      // Update name lookup
      setQbNameLookup(prev => ({ ...prev, [proposal.qb_id]: proposal.qb_name }));
    } catch (error) {
      console.error('Failed to accept proposal:', error);
      setMessage({ type: 'error', text: 'Failed to link customer' });
    }
  };

  const handleRejectProposal = (appId: number) => {
    setRejectedProposals(prev => { const n = new Set(Array.from(prev)); n.add(appId); return n; });
  };

  const handleAcceptAllHigh = async () => {
    const highConfidence = aiProposals.filter(p =>
      p.confidence === 'high' && !acceptedProposals.has(p.app_id) && !rejectedProposals.has(p.app_id)
    );
    for (const p of highConfidence) {
      await handleAcceptProposal(p);
    }
    setMessage({
      type: 'success',
      text: `Accepted ${highConfidence.length} high-confidence matches`
    });
  };

  const handleUnlinkAll = async () => {
    if (!window.confirm('Unlink ALL QuickBooks customer mappings? This will clear every customer\'s QB link. You can re-run Auto-Match afterwards.')) {
      return;
    }
    setUnlinking(true);
    setMessage(null);
    try {
      const result = await unlinkAllQBCustomers();
      setMatchConfidences({});
      setMessage({
        type: 'success',
        text: `Unlinked ${result.unlinked_count} customer mappings. Run Auto-Match to re-link with tighter matching.`
      });
      await handleLoadCustomers();
    } catch (error) {
      console.error('Failed to unlink:', error);
      setMessage({ type: 'error', text: 'Failed to unlink customers' });
    } finally {
      setUnlinking(false);
    }
  };

  const handleLinkCustomer = async (customerId: number, qbCustomerId: string | null) => {
    try {
      await linkQBCustomer(customerId, qbCustomerId);
      // Update local state
      setAppCustomers(prev =>
        prev.map(c => c.id === customerId ? { ...c, qb_customer_id: qbCustomerId } : c)
      );
    } catch (error) {
      console.error('Failed to link customer:', error);
      setMessage({ type: 'error', text: 'Failed to link customer' });
    }
  };

  const filteredAppCustomers = appCustomers.filter(c => {
    if (customerFilter === 'linked' && !c.qb_customer_id) return false;
    if (customerFilter === 'unlinked' && c.qb_customer_id) return false;
    if (customerSearch) {
      const search = customerSearch.toLowerCase();
      return c.name.toLowerCase().includes(search) || c.email.toLowerCase().includes(search);
    }
    return true;
  });

  const linkedCount = appCustomers.filter(c => c.qb_customer_id).length;
  const unlinkedCount = appCustomers.filter(c => !c.qb_customer_id).length;

  const getSelectedQbId = (itemId: number | null): string => {
    if (!itemId) return '';
    const item = items.find(i => i.id === itemId);
    return item?.qb_id || '';
  };

  const formatDate = (dateStr: string | undefined) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getExpirationStatus = () => {
    if (!status) return null;

    // If backend says needs_reconnect, refresh token is expired (100-day limit)
    if (status.needs_reconnect) {
      return { text: 'Connection expired - please reconnect', color: '#ef4444' };
    }

    // Use refresh_token_expires_at (100 days) for connection health, NOT access token (1 hour)
    if (status.refresh_token_expires_at) {
      const refreshExpiresAt = new Date(status.refresh_token_expires_at);
      const now = new Date();
      const daysUntilExpiry = Math.ceil((refreshExpiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      if (daysUntilExpiry < 0) {
        return { text: 'Connection expired - please reconnect', color: '#ef4444' };
      } else if (daysUntilExpiry <= 14) {
        return { text: `Connection renews in ${daysUntilExpiry} days`, color: '#f59e0b' };
      }
      return { text: `Connected (renews in ${daysUntilExpiry} days)`, color: '#22c55e' };
    }

    // Fallback if refresh_token_expires_at not available
    if (status.company_info_error) {
      return { text: 'Connection issue - try reconnecting', color: '#f59e0b' };
    }

    return { text: 'Connected', color: '#22c55e' };
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <h1 style={styles.pageTitle}>QuickBooks Integration</h1>
        <div style={styles.loading}>Loading...</div>
      </div>
    );
  }

  const expirationStatus = getExpirationStatus();

  return (
    <div style={styles.container}>
      <h1 style={styles.pageTitle}>QuickBooks Integration</h1>

      {message && (
        <div style={{
          ...styles.message,
          backgroundColor: message.type === 'success' ? '#f0fdf4' : '#fef2f2',
          borderColor: message.type === 'success' ? '#86efac' : '#fecaca',
          color: message.type === 'success' ? '#166534' : '#dc2626',
        }}>
          {message.type === 'success' ? '\u2713' : '\u2717'} {message.text}
        </div>
      )}

      {/* Connection Status */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <span style={styles.icon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="10" stroke="#2CA01C" strokeWidth="2"/>
              <path d="M8 12L11 15L16 9" stroke="#2CA01C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          Connection Status
        </h2>

        {status?.connected ? (
          <div style={styles.connectedStatus}>
            <div style={styles.statusBadge}>
              <span style={styles.statusDot}></span>
              Connected
            </div>

            <div style={styles.companyInfo}>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Company:</span>
                <span style={styles.infoValue}>{status.company_name || 'Unknown'}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Company ID:</span>
                <span style={styles.infoValue}>{status.company_id || 'N/A'}</span>
              </div>
              <div style={styles.infoRow}>
                <span style={styles.infoLabel}>Last Synced:</span>
                <span style={styles.infoValue}>{formatDate(status.last_synced_at)}</span>
              </div>
              {expirationStatus && (
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Token Status:</span>
                  <span style={{ ...styles.infoValue, color: expirationStatus.color }}>
                    {expirationStatus.text}
                  </span>
                </div>
              )}
            </div>

            <div style={styles.buttonGroup}>
              <button
                onClick={handleConnect}
                disabled={connecting}
                style={styles.reconnectButton}
              >
                {connecting ? 'Connecting...' : 'Reconnect'}
              </button>
              <button
                onClick={handleDisconnect}
                disabled={disconnecting}
                style={styles.disconnectButton}
              >
                {disconnecting ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </div>
          </div>
        ) : (
          <div style={styles.disconnectedStatus}>
            <div style={styles.disconnectedBadge}>
              <span style={styles.disconnectedDot}></span>
              Not Connected
            </div>

            <p style={styles.description}>
              Connect your QuickBooks Online account to automatically sync invoices,
              track payments, and manage billing directly from this portal.
            </p>

            <button
              onClick={handleConnect}
              disabled={connecting}
              style={styles.connectButton}
            >
              {connecting ? 'Connecting...' : 'Connect QuickBooks'}
            </button>
          </div>
        )}
      </div>

      {/* Features Section */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>
          <span style={styles.icon}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
          Integration Features
        </h2>

        <div style={styles.featuresList}>
          <div style={styles.featureItem}>
            <span style={styles.featureIcon}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 4H16M4 8H16M4 12H10" stroke="#2D5A27" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </span>
            <div>
              <strong>Automatic Invoice Creation</strong>
              <p>Invoices are automatically created in QuickBooks when orders are placed.</p>
            </div>
          </div>

          <div style={styles.featureItem}>
            <span style={styles.featureIcon}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 2V18M18 10L2 10" stroke="#2D5A27" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </span>
            <div>
              <strong>Shipping Cost Review</strong>
              <p>Review tasks are created for Sibyl to add shipping costs before sending invoices.</p>
            </div>
          </div>

          <div style={styles.featureItem}>
            <span style={styles.featureIcon}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="6" stroke="#2D5A27" strokeWidth="2"/>
                <path d="M10 7V10L12 12" stroke="#2D5A27" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </span>
            <div>
              <strong>Payment Sync to QB</strong>
              <p>Payments recorded via Helcim or manually are automatically pushed to QuickBooks for bookkeeping.</p>
            </div>
          </div>

          <div style={styles.featureItem}>
            <span style={styles.featureIcon}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="5" width="14" height="10" rx="2" stroke="#2D5A27" strokeWidth="2"/>
                <path d="M3 9H17" stroke="#2D5A27" strokeWidth="2"/>
              </svg>
            </span>
            <div>
              <strong>One-Way Accounting Sync</strong>
              <p>Our app is the source of truth. Invoices, customers, and payments are pushed to QB for P&L and tax reporting.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Pending Sync - only show when connected */}
      {status?.connected && syncStatus && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              <span style={styles.icon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 4V9H4.58152M19.9381 11C19.446 7.05369 16.0796 4 12 4C8.64262 4 5.76829 6.06817 4.58152 9M4.58152 9H9M20 20V15H19.4185M19.4185 15C18.2317 17.9318 15.3574 20 12 20C7.92038 20 4.55399 16.9463 4.06189 13M19.4185 15H15" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              Sync Status
            </h2>
            <button
              onClick={handleRetrySync}
              disabled={retrying || syncStatus.total_pending === 0}
              style={{
                ...styles.syncButton,
                opacity: syncStatus.total_pending === 0 ? 0.5 : 1,
              }}
            >
              {retrying ? 'Syncing...' : 'Sync Now'}
            </button>
          </div>
          {syncStatus.total_pending > 0 ? (
            <div style={{ padding: '0.75rem 1rem', backgroundColor: '#fffbeb', borderRadius: '8px', border: '1px solid #fcd34d' }}>
              <strong style={{ color: '#92400e' }}>{syncStatus.total_pending} pending</strong>
              <span style={{ color: '#78350f', marginLeft: '0.5rem' }}>
                ({syncStatus.pending_invoices} invoice{syncStatus.pending_invoices !== 1 ? 's' : ''}, {syncStatus.pending_customers} customer{syncStatus.pending_customers !== 1 ? 's' : ''})
              </span>
            </div>
          ) : (
            <div style={{ padding: '0.75rem 1rem', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
              <span style={{ color: '#166534' }}>All records synced to QuickBooks</span>
            </div>
          )}
        </div>
      )}

      {/* Item Settings - only show when connected */}
      {status?.connected && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              <span style={styles.icon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 7L12 3L4 7M20 7L12 11M20 7V17L12 21M12 11L4 7M12 11V21M4 7V17L12 21" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              Invoice Item Settings
            </h2>
            <button
              onClick={handleSyncItems}
              disabled={syncing}
              style={styles.syncButton}
            >
              {syncing ? 'Syncing...' : 'Sync Items from QB'}
            </button>
          </div>

          <p style={styles.description}>
            Configure default QuickBooks items for invoice line items.
            {items.length > 0 && ` (${items.length} items available)`}
          </p>

          {items.length === 0 ? (
            <div style={styles.emptyItems}>
              <p>No QuickBooks items synced yet. Click "Sync Items from QB" to import your products and services.</p>
            </div>
          ) : (
            <div style={styles.defaultItemsGrid}>
              <div style={styles.defaultItemRow}>
                <label style={styles.defaultItemLabel}>Default Feed Item:</label>
                <select
                  value={getSelectedQbId(qbSettings?.default_feed_item || null)}
                  onChange={(e) => handleDefaultItemChange('default_feed_item', e.target.value || null)}
                  disabled={savingSettings}
                  style={styles.defaultItemSelect}
                >
                  <option value="">-- None (use item name) --</option>
                  {items.map(item => (
                    <option key={item.qb_id} value={item.qb_id}>
                      {item.name} {item.item_type !== 'Service' ? `(${item.item_type})` : ''}
                    </option>
                  ))}
                </select>
                <span style={styles.defaultItemHelp}>Used when a feed formula has no specific mapping</span>
              </div>

              <div style={styles.defaultItemRow}>
                <label style={styles.defaultItemLabel}>Shipping Item:</label>
                <select
                  value={getSelectedQbId(qbSettings?.shipping_item || null)}
                  onChange={(e) => handleDefaultItemChange('shipping_item', e.target.value || null)}
                  disabled={savingSettings}
                  style={styles.defaultItemSelect}
                >
                  <option value="">-- None --</option>
                  {items.map(item => (
                    <option key={item.qb_id} value={item.qb_id}>
                      {item.name} {item.item_type !== 'Service' ? `(${item.item_type})` : ''}
                    </option>
                  ))}
                </select>
                <span style={styles.defaultItemHelp}>Used for shipping cost line items</span>
              </div>

              <div style={styles.defaultItemRow}>
                <label style={styles.defaultItemLabel}>Additional Fee Item:</label>
                <select
                  value={getSelectedQbId(qbSettings?.additional_fee_item || null)}
                  onChange={(e) => handleDefaultItemChange('additional_fee_item', e.target.value || null)}
                  disabled={savingSettings}
                  style={styles.defaultItemSelect}
                >
                  <option value="">-- None --</option>
                  {items.map(item => (
                    <option key={item.qb_id} value={item.qb_id}>
                      {item.name} {item.item_type !== 'Service' ? `(${item.item_type})` : ''}
                    </option>
                  ))}
                </select>
                <span style={styles.defaultItemHelp}>Used for additional fees on invoices</span>
              </div>
            </div>
          )}

          <div style={styles.itemMappingLink}>
            <Link to="/settings/quickbooks/item-mapping" style={styles.mappingButton}>
              Manage Feed Formula Mappings
            </Link>
            <span style={styles.mappingHelp}>
              Map each feed formula to a specific QuickBooks item
            </span>
          </div>
        </div>
      )}

      {/* Customer Mapping - only show when connected */}
      {status?.connected && (
        <div style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              <span style={styles.icon}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="9" cy="7" r="4" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89318 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              Customer Mapping
            </h2>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleAIMatch}
                disabled={aiMatching || autoMatching || loadingCustomers || unlinking}
                style={{
                  ...styles.syncButton,
                  backgroundColor: aiMatching ? '#e0e7ff' : '#eef2ff',
                  color: '#4338ca',
                  border: '1px solid #a5b4fc',
                }}
              >
                {aiMatching ? 'AI Matching...' : 'AI Match'}
              </button>
              <button
                onClick={handleAutoMatch}
                disabled={autoMatching || aiMatching || loadingCustomers || unlinking}
                style={styles.syncButton}
              >
                {autoMatching ? 'Matching...' : 'Quick Match'}
              </button>
              {linkedCount > 0 && (
                <button
                  onClick={handleUnlinkAll}
                  disabled={unlinking || autoMatching || loadingCustomers}
                  style={{ ...styles.syncButton, backgroundColor: '#fef2f2', color: '#991b1b', border: '1px solid #fca5a5' }}
                >
                  {unlinking ? 'Unlinking...' : 'Unlink All'}
                </button>
              )}
              <button
                onClick={handleLoadCustomers}
                disabled={loadingCustomers}
                style={styles.syncButton}
              >
                {loadingCustomers ? 'Loading...' : appCustomers.length > 0 ? 'Refresh' : 'Load Customers'}
              </button>
            </div>
          </div>

          <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: '0 0 1rem' }}>
            Link your app customers to their QuickBooks counterparts so invoices sync to the correct QB account.
          </p>

          {appCustomers.length > 0 && (
            <>
              {/* Summary badges */}
              <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <span style={{
                  padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 600,
                  backgroundColor: '#f0fdf4', color: '#166534', border: '1px solid #86efac',
                }}>
                  {linkedCount} linked
                </span>
                <span style={{
                  padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 600,
                  backgroundColor: unlinkedCount > 0 ? '#fffbeb' : '#f0fdf4',
                  color: unlinkedCount > 0 ? '#92400e' : '#166534',
                  border: `1px solid ${unlinkedCount > 0 ? '#fcd34d' : '#86efac'}`,
                }}>
                  {unlinkedCount} unlinked
                </span>
                {qbCustomers.length > 0 && (
                  <span style={{
                    padding: '0.25rem 0.75rem', borderRadius: '12px', fontSize: '0.8rem', fontWeight: 600,
                    backgroundColor: '#eff6ff', color: '#1e40af', border: '1px solid #93c5fd',
                  }}>
                    {qbCustomers.length} QB customers loaded
                  </span>
                )}
              </div>

              {/* Filter + search */}
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                <select
                  value={customerFilter}
                  onChange={(e) => setCustomerFilter(e.target.value as 'all' | 'linked' | 'unlinked')}
                  style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.85rem' }}
                >
                  <option value="all">All ({appCustomers.length})</option>
                  <option value="unlinked">Unlinked ({unlinkedCount})</option>
                  <option value="linked">Linked ({linkedCount})</option>
                </select>
                <input
                  type="text"
                  placeholder="Search customers..."
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  style={{ padding: '0.4rem 0.75rem', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.85rem', flex: 1, minWidth: '150px' }}
                />
              </div>

              {/* AI Match proposals */}
              {aiProposals.length > 0 && (
                <div style={{ marginBottom: '1rem', border: '1px solid #a5b4fc', borderRadius: '8px', backgroundColor: '#eef2ff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', borderBottom: '1px solid #c7d2fe' }}>
                    <span style={{ fontWeight: 600, color: '#4338ca', fontSize: '0.9rem' }}>
                      AI Match Proposals ({aiProposals.filter(p => !acceptedProposals.has(p.app_id) && !rejectedProposals.has(p.app_id)).length} pending review)
                    </span>
                    <button
                      onClick={handleAcceptAllHigh}
                      disabled={aiProposals.filter(p => p.confidence === 'high' && !acceptedProposals.has(p.app_id) && !rejectedProposals.has(p.app_id)).length === 0}
                      style={{
                        padding: '0.3rem 0.75rem', fontSize: '0.8rem', fontWeight: 500,
                        backgroundColor: '#f0fdf4', color: '#166534', border: '1px solid #86efac',
                        borderRadius: '6px', cursor: 'pointer',
                      }}
                    >
                      Accept All High Confidence
                    </button>
                  </div>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {aiProposals
                      .filter(p => !acceptedProposals.has(p.app_id) && !rejectedProposals.has(p.app_id))
                      .map(p => (
                        <div key={p.app_id} style={{
                          display: 'flex', alignItems: 'center', gap: '0.5rem',
                          padding: '0.5rem 0.75rem', borderBottom: '1px solid #e0e7ff', fontSize: '0.85rem',
                        }}>
                          <div style={{ flex: 1 }}>
                            <span style={{ fontWeight: 500, color: '#111827' }}>{p.app_name}</span>
                            <span style={{ color: '#6b7280', margin: '0 0.5rem' }}>&rarr;</span>
                            <span style={{ fontWeight: 500, color: '#4338ca' }}>{p.qb_name}</span>
                          </div>
                          <span style={{
                            padding: '0.1rem 0.4rem', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 600,
                            backgroundColor: p.confidence === 'high' ? '#dcfce7' : '#fef3c7',
                            color: p.confidence === 'high' ? '#166534' : '#92400e',
                          }}>
                            {p.confidence}
                          </span>
                          <span style={{ fontSize: '0.7rem', color: '#6b7280', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {p.reason}
                          </span>
                          <button
                            onClick={() => handleAcceptProposal(p)}
                            style={{
                              padding: '0.2rem 0.5rem', fontSize: '0.75rem', fontWeight: 500,
                              backgroundColor: '#f0fdf4', color: '#166534', border: '1px solid #86efac',
                              borderRadius: '4px', cursor: 'pointer',
                            }}
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => handleRejectProposal(p.app_id)}
                            style={{
                              padding: '0.2rem 0.5rem', fontSize: '0.75rem', fontWeight: 500,
                              backgroundColor: '#fef2f2', color: '#991b1b', border: '1px solid #fca5a5',
                              borderRadius: '4px', cursor: 'pointer',
                            }}
                          >
                            Skip
                          </button>
                        </div>
                      ))}
                    {aiProposals.filter(p => !acceptedProposals.has(p.app_id) && !rejectedProposals.has(p.app_id)).length === 0 && (
                      <div style={{ padding: '0.75rem', textAlign: 'center', color: '#6b7280', fontSize: '0.85rem' }}>
                        All proposals reviewed!
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Customer table */}
              <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f9fafb', position: 'sticky', top: 0 }}>
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb' }}>Customer</th>
                      <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid #e5e7eb', minWidth: '200px' }}>QuickBooks Match</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAppCustomers.slice(0, 100).map(cust => (
                      <tr key={cust.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '0.5rem 0.75rem' }}>
                          <div style={{ fontWeight: 500, color: '#111827' }}>{cust.name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            {cust.email}{cust.city ? ` \u2022 ${cust.city}, ${cust.state}` : ''}
                          </div>
                        </td>
                        <td style={{ padding: '0.5rem 0.75rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <div style={{ flex: 1, position: 'relative' }}>
                              {cust.qb_customer_id && qbDropdownOpen !== cust.id ? (
                                <div style={{
                                  display: 'flex', alignItems: 'center', gap: '0.25rem',
                                  padding: '0.35rem 0.5rem', borderRadius: '6px', fontSize: '0.8rem',
                                  border: '1px solid #86efac', backgroundColor: '#f0fdf4', cursor: 'pointer',
                                }}
                                  onClick={() => { setQbDropdownOpen(cust.id); setQbDropdownSearch(''); }}
                                >
                                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {qbNameLookup[cust.qb_customer_id || ''] || qbCustomers.find(q => q.id === cust.qb_customer_id)?.display_name || `QB #${cust.qb_customer_id}`}
                                  </span>
                                  <span
                                    onClick={(e) => { e.stopPropagation(); handleLinkCustomer(cust.id, null); }}
                                    style={{ cursor: 'pointer', color: '#991b1b', fontWeight: 700, fontSize: '0.9rem', lineHeight: 1 }}
                                    title="Unlink"
                                  >&times;</span>
                                </div>
                              ) : (
                                <>
                                  <input
                                    type="text"
                                    placeholder="Search QB customers..."
                                    value={qbDropdownOpen === cust.id ? qbDropdownSearch : ''}
                                    onFocus={() => { setQbDropdownOpen(cust.id); setQbDropdownSearch(''); }}
                                    onChange={(e) => setQbDropdownSearch(e.target.value)}
                                    onBlur={() => setTimeout(() => setQbDropdownOpen(null), 200)}
                                    style={{
                                      width: '100%', padding: '0.35rem 0.5rem', borderRadius: '6px', fontSize: '0.8rem',
                                      border: '1px solid #fcd34d', backgroundColor: '#fffbeb', boxSizing: 'border-box',
                                    }}
                                  />
                                  {qbDropdownOpen === cust.id && (
                                    <div style={{
                                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                                      backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px',
                                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: '200px', overflowY: 'auto',
                                    }}>
                                      {qbCustomers
                                        .filter(qbc => {
                                          if (!qbDropdownSearch) return true;
                                          const s = qbDropdownSearch.toLowerCase();
                                          return qbc.display_name.toLowerCase().includes(s)
                                            || qbc.company_name.toLowerCase().includes(s)
                                            || qbc.email.toLowerCase().includes(s);
                                        })
                                        .slice(0, 30)
                                        .map(qbc => (
                                          <div
                                            key={qbc.id}
                                            onMouseDown={(e) => { e.preventDefault(); handleLinkCustomer(cust.id, qbc.id); setQbDropdownOpen(null); }}
                                            style={{ padding: '0.4rem 0.6rem', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: '0.8rem' }}
                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0fdf4'}
                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                          >
                                            <div style={{ fontWeight: 500 }}>{qbc.display_name}</div>
                                            {(qbc.email || qbc.company_name) && (
                                              <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
                                                {qbc.email}{qbc.company_name && qbc.company_name !== qbc.display_name ? ` | ${qbc.company_name}` : ''}
                                              </div>
                                            )}
                                          </div>
                                        ))
                                      }
                                      {qbCustomers.filter(qbc => {
                                        if (!qbDropdownSearch) return true;
                                        const s = qbDropdownSearch.toLowerCase();
                                        return qbc.display_name.toLowerCase().includes(s) || qbc.company_name.toLowerCase().includes(s) || qbc.email.toLowerCase().includes(s);
                                      }).length === 0 && (
                                        <div style={{ padding: '0.4rem 0.6rem', color: '#9ca3af', fontSize: '0.8rem' }}>No matches</div>
                                      )}
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                            {cust.qb_customer_id && matchConfidences[cust.id] && (
                              <span style={{
                                padding: '0.15rem 0.4rem', borderRadius: '4px', fontSize: '0.65rem', fontWeight: 600,
                                whiteSpace: 'nowrap',
                                backgroundColor: matchConfidences[cust.id] === 'email' ? '#dbeafe' : '#e0e7ff',
                                color: matchConfidences[cust.id] === 'email' ? '#1e40af' : '#3730a3',
                              }}>
                                {matchConfidences[cust.id]}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredAppCustomers.length === 0 && (
                      <tr>
                        <td colSpan={2} style={{ padding: '1rem', textAlign: 'center', color: '#9ca3af' }}>
                          {customerSearch ? 'No customers match your search' : 'No customers in this category'}
                        </td>
                      </tr>
                    )}
                    {filteredAppCustomers.length > 100 && (
                      <tr>
                        <td colSpan={2} style={{ padding: '0.5rem 0.75rem', textAlign: 'center', color: '#6b7280', fontSize: '0.8rem' }}>
                          Showing first 100 of {filteredAppCustomers.length} — use search to narrow down
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {appCustomers.length === 0 && !loadingCustomers && (
            <div style={{ padding: '1rem', backgroundColor: '#f9fafb', borderRadius: '8px', textAlign: 'center', color: '#6b7280' }}>
              Click "Load Customers" to fetch customer data from QuickBooks and your app.
            </div>
          )}
        </div>
      )}

      {/* Quick Links */}
      {status?.connected && (
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>
            <span style={styles.icon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            Quick Links
          </h2>

          <div style={styles.quickLinks}>
            <a href="/sales/invoices" style={styles.quickLink}>
              Invoices
            </a>
            <a
              href="https://app.qbo.intuit.com"
              target="_blank"
              rel="noopener noreferrer"
              style={styles.quickLink}
            >
              Open QuickBooks Online
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '2rem',
  },
  pageTitle: {
    fontSize: '1.75rem',
    fontWeight: 700,
    color: '#1f2937',
    marginBottom: '2rem',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    color: '#6b7280',
  },
  message: {
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    border: '1px solid',
    marginBottom: '1.5rem',
    fontSize: '0.9rem',
  },
  section: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: '#374151',
    margin: '0 0 1rem 0',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  icon: {
    display: 'flex',
    alignItems: 'center',
  },
  connectedStatus: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    backgroundColor: '#f0fdf4',
    color: '#166534',
    borderRadius: '9999px',
    fontWeight: 600,
    fontSize: '0.9rem',
    width: 'fit-content',
  },
  statusDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#22c55e',
  },
  disconnectedStatus: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
    alignItems: 'flex-start',
  },
  disconnectedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem 1rem',
    backgroundColor: '#fef2f2',
    color: '#991b1b',
    borderRadius: '9999px',
    fontWeight: 600,
    fontSize: '0.9rem',
  },
  disconnectedDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: '#ef4444',
  },
  description: {
    color: '#6b7280',
    lineHeight: 1.6,
  },
  companyInfo: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
    padding: '1rem',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
  },
  infoRow: {
    display: 'flex',
    gap: '0.5rem',
  },
  infoLabel: {
    color: '#6b7280',
    fontWeight: 500,
    minWidth: '100px',
  },
  infoValue: {
    color: '#1f2937',
    fontWeight: 500,
  },
  buttonGroup: {
    display: 'flex',
    gap: '1rem',
    marginTop: '0.5rem',
  },
  connectButton: {
    padding: '0.75rem 1.5rem',
    fontSize: '1rem',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #2CA01C 0%, #1E8E14 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  reconnectButton: {
    padding: '0.75rem 1.5rem',
    fontSize: '0.9rem',
    fontWeight: 600,
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  disconnectButton: {
    padding: '0.75rem 1.5rem',
    fontSize: '0.9rem',
    fontWeight: 600,
    backgroundColor: 'transparent',
    color: '#dc2626',
    border: '1px solid #dc2626',
    borderRadius: '8px',
    cursor: 'pointer',
  },
  featuresList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
  },
  featureItem: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-start',
    padding: '0.75rem',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
  },
  featureIcon: {
    flexShrink: 0,
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0fdf4',
    borderRadius: '8px',
  },
  quickLinks: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '1rem',
  },
  quickLink: {
    padding: '0.75rem 1.25rem',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    textDecoration: 'none',
    borderRadius: '8px',
    fontWeight: 500,
    fontSize: '0.9rem',
    transition: 'background-color 0.2s',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  syncButton: {
    padding: '0.5rem 1rem',
    fontSize: '0.85rem',
    fontWeight: 500,
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  emptyItems: {
    padding: '1rem',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    color: '#6b7280',
    textAlign: 'center' as const,
  },
  defaultItemsGrid: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
    marginTop: '1rem',
  },
  defaultItemRow: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
  },
  defaultItemLabel: {
    fontWeight: 600,
    color: '#374151',
    fontSize: '0.9rem',
  },
  defaultItemSelect: {
    padding: '0.5rem',
    fontSize: '0.9rem',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    backgroundColor: 'white',
    cursor: 'pointer',
    maxWidth: '400px',
  },
  defaultItemHelp: {
    fontSize: '0.8rem',
    color: '#9ca3af',
  },
  itemMappingLink: {
    marginTop: '1.5rem',
    paddingTop: '1rem',
    borderTop: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    flexWrap: 'wrap' as const,
  },
  mappingButton: {
    padding: '0.75rem 1.25rem',
    background: 'linear-gradient(135deg, #2CA01C 0%, #1E8E14 100%)',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '8px',
    fontWeight: 600,
    fontSize: '0.9rem',
  },
  mappingHelp: {
    color: '#6b7280',
    fontSize: '0.9rem',
  },
};

export default QuickBooksSettings;
